// src/pages/OrdersDeletePage.tsx
import React, { useEffect, useState } from "react";
import { apiUrl } from "../config/api";

type DeleteState = "idle" | "loading" | "success" | "error";
type RecentOrder = {
  bill_no: string;
  created_at?: string | null;
  payment_method?: string | null;
  sale_channel?: string | null;
  total_amount?: number | string | null;
};

function formatDateTime(value?: string | null): string {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleString("th-TH", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

function formatAmount(value?: number | string | null): string {
  const num = Number(value ?? 0);
  if (!Number.isFinite(num)) return "-";
  return num.toLocaleString("th-TH", {
    style: "currency",
    currency: "THB",
  });
}

export default function OrdersDeletePage(): JSX.Element {
  const [billNo, setBillNo] = useState<string>("");
  const [confirmText, setConfirmText] = useState<string>("");
  const [status, setStatus] = useState<DeleteState>("idle");
  const [errorMsg, setErrorMsg] = useState<string>("");
  const [recentOrders, setRecentOrders] = useState<RecentOrder[]>([]);
  const [loadingRecent, setLoadingRecent] = useState<boolean>(false);
  const [recentError, setRecentError] = useState<string>("");
  const [selectedBillNos, setSelectedBillNos] = useState<string[]>([]);

  const trimmedBillNo = billNo.trim();
  const canDelete =
    trimmedBillNo.length > 0 &&
    confirmText.trim() === trimmedBillNo &&
    status !== "loading";
  const canDeleteSelected =
    selectedBillNos.length > 0 && status !== "loading";

  useEffect(() => {
    void loadRecentOrders();
  }, []);

  const loadRecentOrders = async (): Promise<void> => {
    setLoadingRecent(true);
    setRecentError("");

    try {
      const resp = await fetch(apiUrl("/orders"));
      const data = await resp.json().catch(() => []);
      if (!resp.ok || !Array.isArray(data)) {
        throw new Error("โหลดรายการบิลล่าสุดไม่สำเร็จ");
      }

      setRecentOrders(data.slice(0, 20));
    } catch (err: unknown) {
      setRecentError(
        err instanceof Error ? err.message : "โหลดรายการบิลล่าสุดไม่สำเร็จ"
      );
    } finally {
      setLoadingRecent(false);
    }
  };

  // PMN-CRITICAL(/OrdersDeletePage): single-bill delete is destructive and relies on exact bill confirmation.
  const handleDelete = async () => {
    if (!canDelete) return;
    setStatus("loading");
    setErrorMsg("");

    try {
      const resp = await fetch(
        apiUrl(`/orders/${encodeURIComponent(trimmedBillNo)}`),
        { method: "DELETE" }
      );

      const data = await resp.json().catch(() => ({}));

      if (!resp.ok) {
        const msg: string =
          data?.error ??
          (resp.status === 404
            ? "ไม่พบบิลนี้ในระบบ"
            : resp.statusText || "ลบบิลไม่สำเร็จ");
        setStatus("error");
        setErrorMsg(msg);
        return;
      }

      setStatus("success");
      setErrorMsg("");
      // เคลียร์ฟอร์มหลังลบสำเร็จ
      setBillNo("");
      setConfirmText("");
      setRecentOrders((prev) => prev.filter((order) => order.bill_no !== trimmedBillNo));
      setSelectedBillNos((prev) => prev.filter((value) => value !== trimmedBillNo));
    } catch (err: unknown) {
      setStatus("error");
      setErrorMsg(
        err instanceof Error ? err.message : "เชื่อมต่อเซิร์ฟเวอร์ไม่สำเร็จ"
      );
    }
  };

  // PMN-CRITICAL(/OrdersDeletePage): bulk delete can partially succeed; keep failed bills selected and report each failure.
  const handleDeleteSelected = async () => {
    if (!canDeleteSelected) return;

    setStatus("loading");
    setErrorMsg("");

    const failed: Array<{ billNo: string; message: string }> = [];

    for (const targetBillNo of selectedBillNos) {
      try {
        const resp = await fetch(
          apiUrl(`/orders/${encodeURIComponent(targetBillNo)}`),
          { method: "DELETE" }
        );
        const data = await resp.json().catch(() => ({}));

        if (!resp.ok) {
          failed.push({
            billNo: targetBillNo,
            message:
              data?.error ??
              (resp.status === 404
                ? "ไม่พบบิลนี้ในระบบ"
                : resp.statusText || "ลบบิลไม่สำเร็จ"),
          });
        }
      } catch (err: unknown) {
        failed.push({
          billNo: targetBillNo,
          message: err instanceof Error ? err.message : "เชื่อมต่อเซิร์ฟเวอร์ไม่สำเร็จ",
        });
      }
    }

    const failedSet = new Set(failed.map((item) => item.billNo));
    const deletedCount = selectedBillNos.length - failed.length;

    setRecentOrders((prev) =>
      prev.filter((order) => !selectedBillNos.includes(String(order.bill_no).trim()) || failedSet.has(String(order.bill_no).trim()))
    );

    if (trimmedBillNo && !failedSet.has(trimmedBillNo) && selectedBillNos.includes(trimmedBillNo)) {
      setBillNo("");
      setConfirmText("");
    }

    setSelectedBillNos(failed.map((item) => item.billNo));

    if (failed.length > 0) {
      setStatus("error");
      setErrorMsg(
        deletedCount > 0
          ? `ลบสำเร็จ ${deletedCount} บิล แต่ยังมีที่ลบไม่สำเร็จ: ${failed
              .map((item) => `${item.billNo} (${item.message})`)
              .join(", ")}`
          : `ลบบิลไม่สำเร็จ: ${failed
              .map((item) => `${item.billNo} (${item.message})`)
              .join(", ")}`
      );
      return;
    }

    setStatus("success");
    setErrorMsg("");
  };

  return (
    <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
      <div className="container py-4 py-md-5">
        <div className="row justify-content-center">
          <div className="col-12 col-md-8 col-lg-6">
            <div className="card shadow border-0 rounded-4">
              <div className="card-body p-4 p-md-5">
                <h4 className="mb-3 text-danger text-center">
                  ยกเลิกบิล / ลบบิลขาย
                </h4>
                <p className="small text-muted text-center mb-4">
                  การลบบิลจะ <strong>คืนสต๊อกทั้งหมดของบิลนี้</strong> และลบข้อมูลบิล,
                  รายการสินค้า และส่วนลดของบิลนั้นออกจากระบบ
                </p>

                <div className="mb-4">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6 className="mb-0 text-primary">ย้อนหลัง 20 บิลล่าสุด</h6>
                    {loadingRecent && <span className="small text-muted">กำลังโหลด…</span>}
                  </div>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <div className="small text-muted">
                      เลือกแล้ว {selectedBillNos.length} บิล
                    </div>
                    <div className="d-flex gap-2">
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        disabled={loadingRecent}
                        onClick={() => void loadRecentOrders()}
                      >
                        {loadingRecent ? "กำลังรีเฟรช…" : "รีเฟรช"}
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-danger btn-sm"
                        disabled={!canDeleteSelected}
                        onClick={handleDeleteSelected}
                      >
                        {status === "loading" ? "กำลังลบบิลที่เลือก…" : "ลบที่เลือก"}
                      </button>
                    </div>
                  </div>

                  {recentError && (
                    <div className="alert alert-warning rounded-3 py-2 px-3 mb-2">
                      {recentError}
                    </div>
                  )}

                  <div
                    className="list-group rounded-3 overflow-auto"
                    style={{ maxHeight: "420px" }}
                  >
                    {recentOrders.map((order) => {
                      const normalizedBillNo = String(order.bill_no).trim();
                      const checked = selectedBillNos.includes(normalizedBillNo);

                      return (
                      <button
                        key={order.bill_no}
                        type="button"
                        className={`list-group-item list-group-item-action text-start ${
                          trimmedBillNo === normalizedBillNo ? "active" : ""
                        }`}
                        onClick={() => {
                          const nextBillNo = normalizedBillNo;
                          setBillNo(nextBillNo);
                          setConfirmText(nextBillNo);
                          setStatus("idle");
                          setErrorMsg("");
                        }}
                      >
                        <div className="d-flex justify-content-between align-items-start gap-3">
                          <div className="d-flex align-items-start gap-2">
                            <input
                              type="checkbox"
                              className="form-check-input mt-1"
                              checked={checked}
                              onChange={(e) => {
                                e.stopPropagation();
                                setSelectedBillNos((prev) =>
                                  e.target.checked
                                    ? [...prev, normalizedBillNo]
                                    : prev.filter((value) => value !== normalizedBillNo)
                                );
                              }}
                              onClick={(e) => e.stopPropagation()}
                            />
                            <div>
                            <div className="fw-semibold">บิล #{order.bill_no}</div>
                            <div className="small opacity-75">
                              {formatDateTime(order.created_at)}
                            </div>
                          </div>
                          </div>
                          <div className="text-end small">
                            <div>{order.payment_method || "-"}</div>
                            <div>{order.sale_channel || "-"}</div>
                            <div className="fw-semibold">{formatAmount(order.total_amount)}</div>
                          </div>
                        </div>
                      </button>
                    )})}
                    {!loadingRecent && recentOrders.length === 0 && (
                      <div className="list-group-item text-center text-muted py-3">
                        ไม่พบบิลย้อนหลัง
                      </div>
                    )}
                  </div>
                </div>

                {/* ฟอร์มกรอกเลขบิล */}
                <div className="mb-3">
                  <label htmlFor="billNo" className="form-label">
                    เลขบิลที่ต้องการยกเลิก
                  </label>
                  <input
                    id="billNo"
                    type="text"
                    className="form-control"
                    placeholder="เช่น 001, 024, 105..."
                    value={billNo}
                    onChange={(e) => setBillNo(e.target.value)}
                  />
                  <div className="form-text">
                    ต้องเป็นเลขเดียวกับที่บันทึกจากหน้า ScanPage
                  </div>
                </div>

                {/* กล่องยืนยัน */}
                <div className="mb-3">
                  <label htmlFor="confirmText" className="form-label">
                    พิมพ์เลขบิลอีกครั้งเพื่อยืนยันการลบ
                  </label>
                  <input
                    id="confirmText"
                    type="text"
                    className="form-control"
                    placeholder="พิมพ์เลขบิลเดิมซ้ำอีกครั้ง"
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                  />
                  <div className="form-text text-danger">
                    ระบบจะยกเลิกบิล{" "}
                    <strong>{trimmedBillNo || "…"}</strong> และคืนสต๊อกทั้งหมดของบิลนี้
                  </div>
                </div>

                {/* สถานะผลลัพธ์ */}
                {status === "success" && (
                  <div className="alert alert-success rounded-3">
                    ลบบิลเรียบร้อย และคืนสต๊อกสำเร็จแล้ว
                  </div>
                )}
                {status === "error" && errorMsg && (
                  <div className="alert alert-danger rounded-3">
                    ลบบิลไม่สำเร็จ: {errorMsg}
                  </div>
                )}

                {/* ปุ่มลบ */}
                <div className="d-grid gap-2 mt-3">
                  <button
                    type="button"
                    className="btn btn-danger btn-lg"
                    disabled={!canDelete}
                    onClick={handleDelete}
                  >
                    {status === "loading" ? "กำลังลบบิล…" : "🗑 ยืนยันลบบิลนี้"}
                  </button>

                  <a href="/" className="btn btn-outline-secondary">
                    ← กลับหน้าเมนูหลัก
                  </a>
                </div>

                <div className="small text-muted mt-3">
                  หมายเหตุ: การยกเลิกบิลนี้จะอ้างอิง trigger FIFO เดิม
                  ในฐานข้อมูลเพื่อคืนสต๊อกไปยัง <code>inventory_movements</code>{" "}
                  โดยอัตโนมัติ
                </div>
              </div>
            </div>

            <div className="text-center small text-muted mt-3">
              ตรวจสอบเลขบิลให้แน่ใจก่อนลบ ระบบไม่มีปุ่ม undo นะ 🥲
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
